import { useEffect } from 'react';
import type { MutableRefObject } from 'react';
import L from 'leaflet';
import type { Vendor, Position } from '../../../types';
import styles from './ScaledMarkerLayer.module.css';

interface Options {
  isEditing: boolean;
  map: L.Map;
  filteredVendorsRef: MutableRefObject<Vendor[]>;
  positionsRef: MutableRefObject<Position[]>;
  vendorsRef: MutableRefObject<Vendor[]>;
  addToSelectionRef: MutableRefObject<((uids: Iterable<string>) => void) | undefined>;
}

// Shift-drag on empty map = lasso select (additive). Plain drag stays
// as Leaflet's pan. Marker pointerdowns stopPropagation, so our
// container-level handler only fires over empty map. Desktop-only;
// touch users can still shift-click markers one at a time.
export function useLasso({
  isEditing,
  map,
  filteredVendorsRef,
  positionsRef,
  vendorsRef,
  addToSelectionRef,
}: Options): void {
  useEffect(() => {
    if (!isEditing) return;
    const mapEl = map.getContainer();

    const onPointerDown = (e: PointerEvent) => {
      if (!e.isPrimary || (e.button !== undefined && e.button !== 0)) return;
      if (!e.shiftKey) return;
      e.preventDefault();
      e.stopPropagation();

      const rect = mapEl.getBoundingClientRect();
      const startX = e.clientX - rect.left;
      const startY = e.clientY - rect.top;
      const pointerId = e.pointerId;
      let curX = startX;
      let curY = startY;

      const wasDragEnabled = map.dragging.enabled();
      map.dragging.disable();

      const rectEl = document.createElement('div');
      rectEl.className = styles.lassoRect!;
      mapEl.appendChild(rectEl);

      const drawRect = () => {
        const x = Math.min(startX, curX);
        const y = Math.min(startY, curY);
        const w = Math.abs(curX - startX);
        const h = Math.abs(curY - startY);
        rectEl.style.transform = `translate(${x}px, ${y}px)`;
        rectEl.style.width = `${w}px`;
        rectEl.style.height = `${h}px`;
      };
      drawRect();

      const onMove = (me: PointerEvent) => {
        if (me.pointerId !== pointerId) return;
        curX = me.clientX - rect.left;
        curY = me.clientY - rect.top;
        drawRect();
      };

      const finish = (committed: boolean) => {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onCancel);
        rectEl.remove();
        if (wasDragEnabled) map.dragging.enable();
        if (!committed) return;
        const minX = Math.min(startX, curX);
        const maxX = Math.max(startX, curX);
        const minY = Math.min(startY, curY);
        const maxY = Math.max(startY, curY);
        // Below 3px — treat as a click with shift held (no-op here so we
        // don't accidentally deselect via zero-size lasso).
        if (maxX - minX < 3 && maxY - minY < 3) return;

        const hits: string[] = [];
        const inRect = (coords: [number, number]): boolean => {
          const pt = map.latLngToContainerPoint(L.latLng(coords[0], coords[1]));
          return pt.x >= minX && pt.x <= maxX && pt.y >= minY && pt.y <= maxY;
        };
        for (const v of filteredVendorsRef.current) {
          if (inRect(v.coordinates)) hits.push(v.positionUid);
        }
        // Unassigned positions (placeholder markers) are also selectable.
        const currentVendors = vendorsRef.current;
        for (const p of positionsRef.current) {
          if (currentVendors.some(v => v.positionUid === p.uid)) continue;
          if (inRect(p.coordinates)) hits.push(p.uid);
        }
        if (hits.length > 0) addToSelectionRef.current?.(hits);
      };

      const onUp = (up: PointerEvent) => {
        if (up.pointerId !== pointerId) return;
        finish(true);
      };
      const onCancel = (c: PointerEvent) => {
        if (c.pointerId !== pointerId) return;
        finish(false);
      };

      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onCancel);
    };

    mapEl.addEventListener('pointerdown', onPointerDown);
    return () => { mapEl.removeEventListener('pointerdown', onPointerDown); };
  // Refs are stable — intentionally omitted from deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, map]);
}
